import { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { PiPencilSimpleLine, PiTrash } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import fleetCostCardIcon from "../../../assets/images/Fleet_Cost.svg";
import activeLoadStatusIcon from "../../../assets/images/Active_load.svg";
import inactiveStatusIcon from "../../../assets/images/Inactive_icon.svg";
import statusDecommissionedIcon from "../../../assets/images/Status Decommissioned.svg";
import statusUnderMaintenanceIcon from "../../../assets/images/Status Under Maintenance.svg";
import totalEquipmentsCardIcon from "../../../assets/images/Total Equipments.svg";
import AssetIdHoverCell from "../../../components/AssetIdHoverCell";
import AssetSummaryCard from "../../../components/AssetSummaryCard";
import FilterExternalDropdown from "../../../components/FilterExternalDropdown";
import MultiLineText from "../../../components/MultiLineText";
import NewTableComponent from "../../../components/NewTableComponent";
import Stats from "../../../components/Stats";
import {
  deleteEquipment,
  fetchCompanyBranchesDropdown,
  fetchEquipmentData,
  fetchEquipmentOverview,
  fetchEquipmentTypesDropdown,
} from "../../../store/Slices/EquipmentSlice";
import {
  AppMainModules,
  AssetsPermission,
  AssetsSubModules,
} from "../../../utils/app-constant";
import { findCollectionByKeys, hasPermission, normalizeOptions } from "../../../utils/helper";
import { activeStatsIcon, partUtilizedIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";
import AssetDeleteModal from "../AssetDeleteModal";
import {
  equipmentTypeOptions,
} from "./equipmentAssets.constants";

const pickFirstText = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

const toComparableNumber = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const findOptionByValue = (options = [], value = "") =>
  options.find((option) => String(option?.value) === String(value)) || null;

const findOptionLabelById = (options = [], id) => {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return "";

  const matchedOption = (Array.isArray(options) ? options : []).find((option) => {
    const optionId = Number(option?.optionId ?? option?.id ?? option?.value);
    return Number.isFinite(optionId) && optionId === normalizedId;
  });

  const matchedText = String(matchedOption?.label || matchedOption?.name || matchedOption?.title || "").trim();
  return matchedText;
};

const resolveRelationLabel = (
  relation,
  fallbackId,
  prefix = "Item",
  options = []
) => {
  const directLabel = pickFirstText(
    relation?.name,
    relation?.title,
    relation?.label,
    relation?.branch_name,
    relation?.equipment_type_name,
    relation?.vendor_name,
    relation?.registration_number
  );

  if (directLabel) return directLabel;

  const dropdownLabel = findOptionLabelById(options, fallbackId);
  if (dropdownLabel) return dropdownLabel;

  const id = toComparableNumber(fallbackId);
  return id && id > 0 ? `${prefix} #${id}` : "";
};

const resolveEquipmentVehicleLabel = (row) => {
  const directLabel = pickFirstText(
    row?.vehicle?.registration_number,
    row?.vehicle?.vehicle_id,
    row?.vehicle?.name
  );

  if (directLabel) return directLabel;

  const vehicleId = toComparableNumber(row?.vehicle_id);
  return vehicleId && vehicleId > 0 ? `VE-${vehicleId}` : "N/A";
};

const resolveTyreLabel = (tyre, fallbackId = "") => {
  const directLabel = pickFirstText(
    tyre?.serial_number,
    tyre?.serialNumber,
    tyre?.tyres_id,
    tyre?.tyre_id,
    tyre?.system_id,
    tyre?.label,
    tyre?.name,
    tyre?.title
  );

  if (directLabel) return directLabel;

  const normalizedTyreId = toComparableNumber(fallbackId);
  return normalizedTyreId ? `TY-${normalizedTyreId}` : "";
};

const resolveEquipmentTyreLabels = (row) => {
  const labels = [];
  const pushLabel = (tyre, fallbackId = "") => {
    const label = resolveTyreLabel(tyre, fallbackId);
    if (label && !labels.includes(label)) {
      labels.push(label);
    }
  };

  if (Array.isArray(row?.current_tyres)) {
    row.current_tyres.forEach((item) => pushLabel(item?.tyre || item, item?.tyre_id));
    if (labels.length) return labels;
  }

  if (Array.isArray(row?.tyre_installed_history)) {
    row.tyre_installed_history
      .filter((item) => item?.current !== false && !item?.removed_at)
      .forEach((item) => pushLabel(item?.tyre || item, item?.tyre_id));

    if (labels.length) return labels;
  }

  if (Array.isArray(row?.asset_axles)) {
    row.asset_axles.forEach((axle) => {
      [
        ["outer_left_tyre", "outer_left_tyre_id"],
        ["inner_left_tyre", "inner_left_tyre_id"],
        ["inner_right_tyre", "inner_right_tyre_id"],
        ["outer_right_tyre", "outer_right_tyre_id"],
        ["left_tyre", "left_tyre_id"],
        ["right_tyre", "right_tyre_id"],
        ["left_inner_tyre", "left_inner_tyre_id"],
        ["left_outer_tyre", "left_outer_tyre_id"],
        ["right_inner_tyre", "right_inner_tyre_id"],
        ["right_outer_tyre", "right_outer_tyre_id"],
      ].forEach(([tyreKey, idKey]) => {
        pushLabel(axle?.[tyreKey], axle?.[idKey]);
      });
    });
  }

  return labels;
};

const resolveEquipmentTypeLabel = (
  row,
  equipmentTypeDropdownOptions = [],
  fallbackEquipment = null
) => {
  const directLabel = pickFirstText(
    row?.equipment_type?.equipment_type_name,
    row?.equipment_type?.name,
    row?.equipment_type_name,
    row?.equipmentType?.name,
    row?.equipmentType,
    row?.type?.name,
    fallbackEquipment?.formData?.equipmentType,
    fallbackEquipment?.equipmentType
  );

  if (directLabel) return directLabel;

  const matchedOption = findOptionByValue(
    equipmentTypeDropdownOptions,
    row?.equipment_type_id
  );

  if (matchedOption?.label) return matchedOption.label;

  const equipmentTypeId = toComparableNumber(row?.equipment_type_id);
  return equipmentTypeId && equipmentTypeId > 0 ? `Type #${equipmentTypeId}` : "--";
};

const mapEquipmentApiRow = (
  row,
  equipmentTypeDropdownOptions = [],
  fallbackEquipment = null,
  {
    manufacturerOptions = [],
    modelOptions = [],
    vendorOptions = [],
  } = {}
) => {
  const purchase = Array.isArray(row?.asset_purchases)
    ? row.asset_purchases[0] || {}
    : row?.asset_purchases || row?.asset_purchase || {};
  const systemId = String(row?.equipment_id || row?.systemId || row?.system_id || row?.id || "").trim();
  const equipmentType = resolveEquipmentTypeLabel(
    row,
    equipmentTypeDropdownOptions,
    fallbackEquipment
  );
  const branchLabel = resolveRelationLabel(row?.branch, row?.branch_id, "Branch");
  const linkedVehicleId = resolveEquipmentVehicleLabel(row);
  const vehicleId = toComparableNumber(row?.vehicle?.id ?? row?.vehicle_id) || "";
  const usageMeterType = pickFirstText(row?.usage_meter_type, row?.usageMeterType);
  const usageMeterReadingValue =
    toComparableNumber(
      row?.usage_meter_reading ??
        row?.usageMeterReading ??
        row?.meter_reading ??
        row?.odm ??
        row?.vehicle?.odm
    ) || 0;
  const capacityValue = toComparableNumber(row?.capacity) || 0;
  const capacityUnit = pickFirstText(row?.capacity_unit, row?.capacityUnit);
  const purchaseCost =
    toComparableNumber(
      purchase?.purchase_cost ??
        purchase?.purchaseCost ??
        row?.purchase_cost ??
        row?.purchaseCost
    ) || 0;
  const linkedTyreLabels = resolveEquipmentTyreLabels(row);

  return {
    ...row,
    id: row?.id,
    isCustom: false,
    systemId,
    equipmentId: systemId,
    registrationNumber: String(row?.registration_number || "").trim() || "-",
    equipmentType: equipmentType || "-",
    serialNumber: String(row?.serial_number || "").trim() || "-",
    capacityValue,
    capacityUnit,
    capacity: `${capacityValue.toLocaleString("en-US")} ${capacityUnit}`.trim() || "-",
    branch: branchLabel || "-",
    linkedVehicleRecord: row?.vehicle || null,
    linkedVehicleId,
    vehicleId,
    linkedTyres: linkedTyreLabels.length || toComparableNumber(row?.number_of_tyres) || 0,
    linkedTyreLabels,
    usageMeterType,
    usageMeterReadingValue,
    status: String(row?.status || "").trim().toLowerCase() || "inactive",
    totalCost: purchaseCost,
  };
};

const toText = (value) => String(value ?? "").trim();

const isNullishDisplayValue = (value) => {
  const normalized = toText(value).toLowerCase();
  return (
    !normalized ||
    normalized === "-" ||
    normalized === "--" ||
    normalized === "n/a" ||
    normalized === "null" ||
    normalized === "undefined" ||
    normalized.includes("nan") ||
    normalized.includes("null") ||
    normalized.includes("undefined") ||
    normalized === "eq-n/a" ||
    normalized === "eq--" ||
    normalized === "eq-"
  );
};

const statusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "active") {
    return {
      color: "#299900",
      label: "Active",
      icon: activeLoadStatusIcon,
    };
  }

  if (normalized === "under_maintenance") {
    return {
      color: "#F2841B",
      label: "Under Maintenance",
      icon: statusUnderMaintenanceIcon,
    };
  }

  if (normalized === "decommissioned") {
    return {
      color: "#0A53E8",
      label: "Decommissioned",
      icon: statusDecommissionedIcon,
    };
  }

  return {
    color: "#EF0000",
    label: "Inactive",
    icon: inactiveStatusIcon,
  };
};

const resolvePositiveNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }

    const trailingDigits = toText(value).match(/(\d+)$/);
    if (!trailingDigits) continue;

    const parsedTrailingDigits = Number(trailingDigits[1]);
    if (Number.isFinite(parsedTrailingDigits) && parsedTrailingDigits > 0) {
      return parsedTrailingDigits;
    }
  }

  return null;
};

const numericOperatorOptions = [
  { label: "Equal To", value: "equal_to" },
  { label: "Less Than", value: "less_than" },
  { label: "Greater Than", value: "greater_than" },
  { label: "Less Than Equal To", value: "less_than_equal_to" },
  { label: "Greater Than Equal To", value: "greater_than_equal_to" },
];

const EQUIPMENT_STATUS_FILTER_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Under Maintenance", value: "under_maintenance" },
  { label: "Inactive", value: "inactive" },
  { label: "Decommissioned", value: "decommissioned" },
];

const hasFilterValue = (value) =>
  !(
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );

const buildEquipmentSearchFilters = (filters = {}) =>
  Object.entries(filters || {}).reduce((mapped, [filterName, filterObj]) => {
    const rawValue = filterObj?.value;

    if (filterObj?.filter_type && hasFilterValue(rawValue)) {
      mapped[filterName] = {
        filter_type: filterObj.filter_type,
        value: rawValue,
      };
    }

    return mapped;
  }, {});

const uniqueMappedOptions = (rows = [], valueAccessor, labelAccessor) => {
  const map = new Map();

  rows.forEach((row) => {
    const rawValue = typeof valueAccessor === "function" ? valueAccessor(row) : "";
    const numericValue = resolvePositiveNumber(rawValue);
    const normalizedValue = numericValue ?? toText(rawValue);
    const uniqueKey = toText(normalizedValue);
    const label = toText(typeof labelAccessor === "function" ? labelAccessor(row) : "");

    if (!uniqueKey || !label || map.has(uniqueKey)) return;

    map.set(uniqueKey, {
      label,
      value: numericValue ?? normalizedValue,
    });
  });

  return Array.from(map.values());
};

const toCompactMillions = (value) => {
  const amount = Number(value) || 0;
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(2)} M`;
  return amount.toLocaleString("en-US");
};

const formatPercent = (part, total) => {
  if (!total) return "0.0%";
  return `${((part / total) * 100).toFixed(1)}%`;
};

const formatOverviewDate = (value) => {
  if (!value) return "";

  const parsedDate = new Date(value);
  if (!Number.isFinite(parsedDate.getTime())) return "";

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const getDefaultTableLimit = () => 10;

const appendAssetsRegistrationTab = (path, tab = "vehicles") => {
  const normalizedTab = ["vehicles", "equipments", "tyres"].includes(
    String(tab || "").trim().toLowerCase()
  )
    ? String(tab || "").trim().toLowerCase()
    : "vehicles";
  const separator = String(path).includes("?") ? "&" : "?";
  return `${path}${separator}tab=${normalizedTab}`;
};

const formatEquipmentCost = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "--";
  return `PKR ${amount.toLocaleString("en-US")}`;
};

const buildEquipmentHoverPreview = (row) => {
  const detailLine = [
    row?.equipmentType,
    row?.formData?.assetNumber ? `Asset ${row.formData.assetNumber}` : "",
  ]
    .filter((value) => !isNullishDisplayValue(value))
    .join(" | ");
  const registrationText = isNullishDisplayValue(row?.registrationNumber)
    ? "--"
    : row.registrationNumber;
  const assetNumberText = isNullishDisplayValue(row?.formData?.assetNumber)
    ? "--"
    : row.formData.assetNumber;
  const axleText = isNullishDisplayValue(row?.formData?.numberOfAxles)
    ? "--"
    : row.formData.numberOfAxles;
  const status = statusMeta(row?.status);

  return {
    assetId: row?.equipmentId,
    title: row?.serialNumber || row?.registrationNumber || row?.equipmentId || "--",
    subtitle: detailLine || row?.description || "Equipment Details",
    statusLabel: status.label,
    statusColor: status.color,
    rows: [
      { label: "Reg. No", value: registrationText },
      { label: "Asset No", value: assetNumberText },
      { label: "Axles", value: axleText },
      { label: "Purchase Cost", value: formatEquipmentCost(row?.totalCost) },
    ],
  };
};

const getEquipmentBranchBadgeClassName = (branch) => {
  const normalizedBranch = String(branch || "").trim().toLowerCase();

  if (normalizedBranch.includes("holding")) {
    return "asset_branch_badge asset_branch_badge_holding";
  }

  return "asset_branch_badge asset_branch_badge_primary";
};

const AssetsEquipmentsTab = ({
  activeTab = "equipments",
  searchText: externalSearchText,
  setSearchText: externalSetSearchText,
  searchFilters: externalSearchFilters,
  setSearchFilters: externalSetSearchFilters,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canUpdateEquipment = hasPermission({
    permissionSlug: AssetsPermission.UpdateEquipment,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const canDeleteEquipment = hasPermission({
    permissionSlug: AssetsPermission.DeleteEquipment,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const {
    data: equipmentApiRows = [],
    loading: equipmentLoading = false,
    error: equipmentError = null,
    meta: equipmentMeta = {},
    companyBranchesDropdownData = [],
    deleteEquipmentLoading = false,
    equipmentTypesDropdownData = [],
    equipmentOverviewData = null,
  } = useSelector((state) => state.equipment);

  const [internalSearchText, setInternalSearchText] = useState("");
  const [internalSearchFilters, setInternalSearchFilters] = useState({});

  const searchText =
    externalSearchText !== undefined ? externalSearchText : internalSearchText;
  const setSearchText = externalSetSearchText || setInternalSearchText;
  const searchFilters =
    externalSearchFilters !== undefined
      ? externalSearchFilters
      : internalSearchFilters;
  const setSearchFilters = externalSetSearchFilters || setInternalSearchFilters;
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(getDefaultTableLimit());
  const [removedEquipmentRows, setRemovedEquipmentRows] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const equipmentApiSearchFilters = useMemo(
    () => buildEquipmentSearchFilters(searchFilters),
    [searchFilters]
  );

  useEffect(() => {
    dispatch(fetchEquipmentOverview());
    dispatch(fetchCompanyBranchesDropdown());
    dispatch(fetchEquipmentTypesDropdown());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, searchFilters, limit]);

  useEffect(() => {
    dispatch(
      fetchEquipmentData({
        page: currentPage,
        limit,
        search_text: searchText,
        searchFilters: equipmentApiSearchFilters,
      })
    );
  }, [currentPage, dispatch, equipmentApiSearchFilters, limit, searchText]);

  const equipmentTypeDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentTypesDropdownData)
      ? equipmentTypesDropdownData
      : findCollectionByKeys(equipmentTypesDropdownData, [
          "equipmenttypes",
          "equipmenttype",
          "equipmenttypedropdown",
          "types",
        ]);

    const normalized = normalizeOptions(source, {
      valueKeys: ["value", "id", "equipment_type_id"],
      labelKeys: ["label", "equipment_type_name", "name", "title"],
      defaultLabelPrefix: "Equipment Type",
    });

    return normalized.length ? normalized : equipmentTypeOptions;
  }, [equipmentTypesDropdownData]);

  const equipmentBranchDropdownOptions = useMemo(() => {
    const source = Array.isArray(companyBranchesDropdownData)
      ? companyBranchesDropdownData
      : findCollectionByKeys(companyBranchesDropdownData, [
          "companybranches",
          "branches",
          "companybranchdropdown",
          "branchdropdown",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "branch_id"],
      labelKeys: ["label", "branch_name", "name", "title"],
      defaultLabelPrefix: "Branch",
    });
  }, [companyBranchesDropdownData]);

  const mappedEquipmentRows = useMemo(
    () =>
      (Array.isArray(equipmentApiRows) ? equipmentApiRows : [])
        .map((row) => mapEquipmentApiRow(row, equipmentTypeDropdownOptions))
        .filter((row) => row.systemId && !removedEquipmentRows.includes(row.systemId)),
    [equipmentApiRows, equipmentTypeDropdownOptions, removedEquipmentRows]
  );

  const allEquipmentRows = useMemo(() => {
    const bySystemId = new Map();

    mappedEquipmentRows.forEach((row) => {
      const key = String(row?.systemId || "").trim();
      if (!key || removedEquipmentRows.includes(key)) return;
      bySystemId.set(key, row);
    });

    return Array.from(bySystemId.values());
  }, [mappedEquipmentRows, removedEquipmentRows]);

  const totalPages = Math.max(1, Number(equipmentMeta?.totalPages) || 1);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const equipmentStats = useMemo(() => {
    const hasApiOverview =
      equipmentOverviewData && typeof equipmentOverviewData === "object";
    const visibleEquipmentCount = allEquipmentRows.length;
    const totalEquipment = hasApiOverview
      ? Number(equipmentOverviewData?.total_equipment) || 0
      : Math.max(Number(equipmentMeta?.totalItems) || 0, visibleEquipmentCount);
    const attachedToVehicle = hasApiOverview
      ? Number(equipmentOverviewData?.attached_to_vehicle) || 0
      : allEquipmentRows.filter((row) => row.linkedVehicleId && row.linkedVehicleId !== "-")
          .length;
    const unattached = hasApiOverview
      ? Number(equipmentOverviewData?.unattached) || 0
      : Math.max(0, visibleEquipmentCount - attachedToVehicle);
    const totalEquipmentCost = hasApiOverview
      ? Number(equipmentOverviewData?.total_equipment_cost) || 0
      : allEquipmentRows.reduce((sum, row) => sum + (Number(row?.totalCost) || 0), 0);
    const lastUpdated = formatOverviewDate(
      hasApiOverview ? equipmentOverviewData?.last_updated : ""
    );

    return {
      totalEquipment,
      visibleEquipmentCount,
      attachedToVehicle,
      unattached,
      totalEquipmentCost,
      attachedPercentage: formatPercent(attachedToVehicle, totalEquipment),
      unattachedPercentage: formatPercent(unattached, totalEquipment),
      lastUpdated,
    };
  }, [allEquipmentRows, equipmentMeta?.totalItems, equipmentOverviewData]);

  const statsData = useMemo(
    () => [
      {
        title: "Total Equipment",
        value: equipmentStats.totalEquipment,
        text: <>Last updated : {equipmentStats.lastUpdated || "--"}</>,
        icon: (
          <img
            src={totalEquipmentsCardIcon}
            alt="Total equipment"
            className="asset_registration_stats_icon"
          />
        ),
      },
      {
        title: "Attached to Vehicle",
        value: equipmentStats.attachedToVehicle,
        text: (
          <span>
            <b>{equipmentStats.attachedPercentage}</b> from total
          </span>
        ),
        icon: activeStatsIcon,
        className: "completed",
      },
      {
        title: "Unattached",
        value: equipmentStats.unattached,
        text: (
          <span>
            <b>{equipmentStats.unattachedPercentage}</b> from total
          </span>
        ),
        icon: partUtilizedIcon,
        className: "late",
      },
      {
        title: "Total Equipment Cost (PKR)",
        value: toCompactMillions(equipmentStats.totalEquipmentCost),
        text: (
          <span>
            <b>{equipmentStats.visibleEquipmentCount}</b> loaded records
          </span>
        ),
        icon: (
          <img
            src={fleetCostCardIcon}
            alt="Total equipment cost"
            className="asset_registration_stats_icon"
          />
        ),
        className: "pending-delivery",
      },
    ],
    [equipmentStats]
  );

  const equipmentOptionSets = useMemo(
    () => ({
      equipmentTypeOptions:
        equipmentTypeDropdownOptions.length
          ? equipmentTypeDropdownOptions
          : uniqueMappedOptions(
              allEquipmentRows,
              (row) => row.equipmentTypeId,
              (row) => row.equipmentType
            ),
      branchOptions:
        equipmentBranchDropdownOptions.length
          ? equipmentBranchDropdownOptions
          : uniqueMappedOptions(allEquipmentRows, (row) => row.branchId, (row) => row.branch),
      linkedVehicleIdOptions: uniqueMappedOptions(
        allEquipmentRows,
        (row) => row.vehicleId,
        (row) => row.linkedVehicleId
      ),
      statusOptions: EQUIPMENT_STATUS_FILTER_OPTIONS,
    }),
    [allEquipmentRows, equipmentBranchDropdownOptions, equipmentTypeDropdownOptions]
  );

  const handleDeleteRow = async (row) => {
    if (Number(row?.id) > 0) {
      try {
        await dispatch(deleteEquipment(row.id)).unwrap();
        successNotify("Equipment deleted successfully");
        dispatch(fetchEquipmentOverview());

        if (allEquipmentRows.length === 1 && currentPage > 1) {
          setCurrentPage((previous) => Math.max(1, previous - 1));
        } else {
          dispatch(
            fetchEquipmentData({
              page: currentPage,
              limit,
              search_text: searchText,
              searchFilters: equipmentApiSearchFilters,
            })
          );
        }
      } catch (error) {
        errorNotify(error);
        return false;
      }

      return true;
    }

    setRemovedEquipmentRows((previous) =>
      previous.includes(row.systemId) ? previous : [...previous, row.systemId]
    );
    return true;
  };

  const closeDeleteModal = () => {
    if (deleteEquipmentLoading) return;
    setDeleteTarget(null);
  };

  const navigateToLinkedVehicle = (row) => {
    const routeId = row?.vehicleId || toText(row?.linkedVehicleId);
    if (!routeId || isNullishDisplayValue(routeId)) return;

    navigate(
      appendAssetsRegistrationTab(
        `/assets/vehicles/${encodeURIComponent(routeId)}`,
        activeTab
      ),
      {
        state: {
          vehicle: {
            id: row?.vehicleId || null,
            vehicle_id: row?.linkedVehicleRecord?.vehicle_id || row?.linkedVehicleId || "",
            systemId: row?.linkedVehicleRecord?.vehicle_id || row?.linkedVehicleId || "",
            registrationNumber: row?.linkedVehicleRecord?.registration_number || "",
            currentOdometerValue: Number(row?.linkedVehicleRecord?.odm) || 0,
            currentOdometer: row?.linkedVehicleRecord?.odm
              ? `${row.linkedVehicleRecord.odm} km`
              : "",
          },
          activeTab,
        },
      }
    );
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const didDelete = await handleDeleteRow(deleteTarget);
    if (didDelete) {
      setDeleteTarget(null);
    }
  };

  const commonColumns = {
    checked: true,
    disabled: true,
    hide: false,
    exportColumn: true,
    sortable: false,
  };

  const columns = [
    {
      ...commonColumns,
      name: "Equipment Id",
      selector: (row, index) => (
        <AssetIdHoverCell
          index={index}
          label={row.equipmentId}
          defaultCardWidth={320}
          defaultCardHeight={210}
          onClick={() =>
            navigate(
              appendAssetsRegistrationTab(
                `/assets/equipments/${encodeURIComponent(row.equipmentId)}`,
                activeTab
              ),
              {
                state: { equipment: row, activeTab },
              }
            )
          }
        >
          <AssetSummaryCard {...buildEquipmentHoverPreview(row)} />
        </AssetIdHoverCell>
      ),
      tableColumn: false,
      filterName: "equipmentIdFilter",
      type: "text",
      minWidth: "120px",
    },
    {
      ...commonColumns,
      name: "Equipment Type",
      selector: (row) => row.equipmentType || "-",
      tableColumn: true,
      filterName: "equipmentTypeFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: equipmentOptionSets.equipmentTypeOptions,
      minWidth: "150px",
    },
    {
      ...commonColumns,
      name: "Serial Number",
      selector: (row) => row.serialNumber,
      tableColumn: true,
      filterName: "serialNumberFilter",
      type: "text",
      operatorOptions: numericOperatorOptions,
      minWidth: "160px",
    },
    {
      ...commonColumns,
      name: "Capacity",
      selector: (row) => row.capacity,
      tableColumn: false,
      filterName: "capacityFilter",
      type: "text",
      operatorOptions: numericOperatorOptions,
      minWidth: "120px",
    },
    {
      ...commonColumns,
      name: "Branch",
      selector: (row) => (
        <span className={getEquipmentBranchBadgeClassName(row.branch)}>
          {row.branch || "-"}
        </span>
      ),
      tableColumn: false,
      filterName: "branchFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: equipmentOptionSets.branchOptions,
      minWidth: "120px",
    },
    {
      ...commonColumns,
      name: "Vehicle Registration No.",
      selector: (row) => {
        if (isNullishDisplayValue(row?.linkedVehicleId)) {
          return <span style={{ color: "#9ca3af" }}>-</span>;
        }

        return (
          <button
            type="button"
            className="assets_equipment_linked_vehicle_chip"
            onClick={() => navigateToLinkedVehicle(row)}
          >
            <span className="service-task">
              <MultiLineText text={row.linkedVehicleId} maxWidth={"90px"} />
            </span>
          </button>
        );
      },
      tableColumn: true,
      filterName: "linkedVehicleIdFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: equipmentOptionSets.linkedVehicleIdOptions,
      minWidth: "170px",
    },
    {
      ...commonColumns,
      name: "Linked Tyres",
      selector: (row) => {
        const serviceItems = Array.isArray(row.linkedTyreLabels)
          ? row.linkedTyreLabels
          : [];
        const count = serviceItems.length || Number(row.linkedTyres) || 0;

        if (count === 0 || !serviceItems.length) {
          return <span style={{ color: "#9ca3af" }}>-</span>;
        }

        if (serviceItems.length === 1) {
          return (
            <span className="service-task">
              <MultiLineText text={serviceItems[0]} maxWidth={"90px"} />
            </span>
          );
        }

        const firstItem = serviceItems[0];
        const remainingCount = serviceItems.length - 1;

        return (
          <div className="service-tasks-container">
            <span className="first-task">
              <MultiLineText text={firstItem} maxWidth={"90px"} />
            </span>

            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <div>
                    {serviceItems.slice(1).map((item, index) => (
                      <div key={index} className="tooltip_item">
                        {item}
                      </div>
                    ))}
                  </div>
                </Tooltip>
              }
            >
              <span className="additional-tasks">+{remainingCount}</span>
            </OverlayTrigger>
          </div>
        );
      },
      tableColumn: false,
      minWidth: "150px",
    },
    {
      ...commonColumns,
      name: "Usage Meter Reading",
      selector: (row) => row.usageMeterReading,
      tableColumn: false,
      filterName: "usageMeterReadingFilter",
      type: "number",
      minWidth: "170px",
    },
    {
      ...commonColumns,
      name: "Next Service Due",
      selector: (row) => row.nextServiceDue,
      tableColumn: false,
      minWidth: "150px",
    },
    {
      ...commonColumns,
      name: "Status",
      selector: (row) => {
        const meta = statusMeta(row.status);

        return (
          <span className="asset_status_cell" style={{ color: meta.color }}>
            <img src={meta.icon} alt={meta.label} className="asset_status_icon" />
            {meta.label}
          </span>
        );
      },
      tableColumn: true,
      filterName: "statusFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: equipmentOptionSets.statusOptions,
      minWidth: "170px",
    },
    {
      ...commonColumns,
      name: "Total Cost (PKR)",
      selector: (row) =>
        row.totalCostDisplay || Number(row.totalCost || 0).toLocaleString("en-US"),
      tableColumn: false,
      minWidth: "145px",
    },
    {
      name: "Action",
      checked: true,
      disabled: false,
      hide: false,
      exportColumn: false,
      sortable: false,
      selector: (row) => (
        <div className="table_action_btn">
          {canUpdateEquipment && (
            <button
              type="button"
              className="edit_btn"
              title="Edit"
              onClick={() =>
                navigate(
                  appendAssetsRegistrationTab(
                    `/assets/equipments/${encodeURIComponent(row.equipmentId)}`,
                    activeTab
                  ),
                  {
                    state: { equipment: row, activeTab },
                  }
                )
              }
            >
              <PiPencilSimpleLine />
            </button>
          )}
          {canDeleteEquipment && (
            <button
              type="button"
              className="delete_btn"
              title="Delete"
              onClick={() => setDeleteTarget(row)}
            >
              <PiTrash />
            </button>
          )}
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
    },
  ];

  return (
    <>
      <AssetDeleteModal
        show={Boolean(deleteTarget)}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        loading={deleteEquipmentLoading}
        heading="Delete Equipment"
        message="Are you sure you want to delete this equipment?"
      />

      <Stats statsData={statsData} />

      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={allEquipmentRows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={Math.max(Number(equipmentMeta?.totalItems) || 0, allEquipmentRows.length)}
        totalPages={totalPages}
        limitOptions={[10, 20, 50, 100]}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={equipmentLoading || deleteEquipmentLoading}
        paginationLoading={equipmentLoading || deleteEquipmentLoading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        tableClassName="assets_registration_table assets_registration_table_equipment"
        noDataComponent={
          <div className="table_no_records">
            {equipmentError || "There are no records to display"}
          </div>
        }
      />
    </>
  );
};

export default AssetsEquipmentsTab;
